/**
 * Nachtrag-Version (docs/PLAN-NACHTRAG-VERSION.md, Einheit 2): die zwei reinen Rechenstellen.
 *
 *   - `nachtragsAusgleich`  Geld: was aendert sich an den Zahlungen, wenn Fassung N+1 gilt?
 *   - `nachtragsVergleich`  Anzeige: was unterscheidet N von N+1 (Positionen, Gaeste, Datum, Summe)?
 *
 * Beide kennen weder Datenbank noch Netz. Die Gast-Vorschau und die Ausfuehrung bei der Zustimmung
 * rufen dieselbe Funktion auf - der Browser rechnet nie selbst. Betraege sind ganzzahlige Cent.
 *
 * DER GRUNDSATZ DES AUSGLEICHS: Bezahltes bleibt und wird angerechnet (netto = Zahlungen minus
 * Erstattungen). Offen ist nur, was die neue Summe ueber das Bezahlte hinaus verlangt. Die offenen
 * Zahlungen werden so neu verteilt, dass bezahlt + offen exakt die neue Summe ergibt.
 */
import { stufeSollCents, verteileNeu, istOffenerZahlungsstatus } from "./zahlungsstaffel";

// ---------------------------------------------------------------------------------------------
// Ausgleich
// ---------------------------------------------------------------------------------------------

export interface NachtragKonditionen {
  /** Anzahlung in Basispunkten (3000 = 30 %). */
  depositPercentBp?: number | null;
  /** Fester Anzahlungsbetrag in Cent; hat Vorrang vor dem Prozentsatz (wie `computeDepositCents`). */
  depositAmountCents?: number | null;
}

export interface NachtragZahlung {
  /** Zeilen-ID der Zahlung; die Route bildet das Ergebnis darauf ab. */
  id: string;
  amountCents: number | null | undefined;
  /** Bereits erstattet (Teil- oder Vollerstattung), Cent. */
  refundedAmountCents?: number | null;
  /** pending / processing = offen; nur succeeded zaehlt als bezahlt (abzueglich refunded_amount_cents); sonst ignoriert. */
  status: string | null | undefined;
  /**
   * Zahlungsart (`payments.payment_type`): deposit/prepayment = Anzahlung, final/full = Restzahlung.
   * Bei OFFENEN Zahlungen Pflicht; fehlt sie oder ist sie unbekannt, bricht der Ausgleich mit
   * `unbekannte_zahlungsart` ab, statt zu raten.
   */
  typ?: string | null;
}

/**
 * WICHTIG fuer den Aufrufer: `zahlungen` muss dieselbe Zahlungsmenge sein, die `event_deckung` /
 * `leseDeckung` benutzt (Variante, externe Zahlungen, Guthaben). Diese Funktion kennt nur, was sie
 * bekommt; eine andere Auswahl rechnet am Ergebnis der Deckung vorbei.
 */
export interface NachtragsAusgleichEingabe {
  altGesamtCents: number;
  neuGesamtCents: number;
  /** Die Konditionen, die ab Zustimmung gelten (bei unveraenderten Konditionen die alten). */
  konditionen: NachtragKonditionen;
  zahlungen: readonly NachtragZahlung[];
}

export type NachtragAktion = "behalten" | "ersetzen" | "neu_anlegen" | "entfallen";

export interface NachtragZahlungsAktion {
  /** null bei `neu_anlegen`. */
  zahlungId: string | null;
  aktion: NachtragAktion;
  typ: string | null;
  altCents: number;
  /** Betrag nach dem Ausgleich; 0 bei `entfallen`. */
  neuCents: number;
}

export type NachtragsAusgleichFehler = "ungueltige_summe" | "unbekannte_zahlungsart";

export type NachtragsAusgleich =
  | {
      ok: true;
      altGesamtCents: number;
      neuGesamtCents: number;
      differenzCents: number;
      /** Anzahlung nach den neuen Konditionen (0, wenn keine definiert ist). */
      sollAnzahlungCents: number;
      /** Summe der erfolgreichen Zahlungen minus Erstattungen. */
      bereitsBezahltNettoCents: number;
      /** Was die neue Summe ueber das Bezahlte hinaus noch verlangt (>= 0). */
      nochZuZahlenCents: number;
      /** Preiserhoehung gegenueber der angenommenen Fassung: max(0, neu - alt). */
      mehrbetragCents: number;
      /** Mehr bezahlt als die neue Summe: max(0, netto - neu). Verrechnen oder erstatten. */
      ueberschussCents: number;
      /**
       * Was ab der Zustimmung sofort faellig ist: die Summe der Anzahlungen NACH dem Ausgleich (offene behaltene,
       * ersetzte und neu angelegte). Bei bereits bezahlter Anzahlung ist das die Differenz zur neuen Soll-Anzahlung.
       */
      faelligNachZustimmungCents: number;
      /** Der Rest nach der sofort faelligen Zahlung: nochZuZahlenCents - faelligNachZustimmungCents (>= 0). */
      restCents: number;
      /** Eine Zeile je offener Zahlung plus ggf. neu anzulegende. */
      aktionen: NachtragZahlungsAktion[];
    }
  | { ok: false; fehler: NachtragsAusgleichFehler; meldung: string };

/**
 * Anzahlung aus Konditionen und Summe. Gleiche Regel wie `computeDepositCents` in
 * `apps/api/src/lib/payment-terms.ts` (fester Betrag vor Prozent, gedeckelt auf die Summe), hier
 * rein, damit der Ausgleich ohne `apps/api` rechnet. `null` heisst: keine Anzahlung definiert.
 */
export function nachtragAnzahlungSollCents(k: NachtragKonditionen, gesamtCents: number): number | null {
  if (k.depositAmountCents != null && k.depositAmountCents > 0) return Math.min(k.depositAmountCents, gesamtCents);
  if (k.depositPercentBp != null && k.depositPercentBp > 0) {
    return gesamtCents > 0 ? stufeSollCents(gesamtCents, { prozentBp: k.depositPercentBp }) : 0;
  }
  return null;
}

function ganz(v: number | null | undefined): number {
  return typeof v === "number" && Number.isFinite(v) ? Math.round(v) : 0;
}

/** Netto-Anteil einer Zahlung am Bezahlten: nur succeeded zaehlt (wie event_deckung), abzueglich Erstattung. */
function nettoBezahlt(z: NachtragZahlung): number {
  if (z.status !== "succeeded") return 0;
  return Math.max(Math.max(ganz(z.amountCents), 0) - Math.max(ganz(z.refundedAmountCents), 0), 0);
}

/** deposit/prepayment -> Anzahlung, final/full -> Restzahlung, sonst null (wie auto-invoice.ts resolveInvoiceType). */
function zahlungsArt(typ: string | null | undefined): "deposit" | "final" | null {
  if (typ === "deposit" || typ === "prepayment") return "deposit";
  if (typ === "final" || typ === "full") return "final";
  return null;
}

interface OffeneZeile {
  id: string;
  typ: string | null;
  altCents: number;
}

/**
 * Verteilt `ziel` Cent auf die Zeilen einer Gruppe und liefert je Zeile die Aktion.
 * Ziel 0 -> alle entfallen. Ziel kleiner als die Zeilenzahl -> die erste traegt alles, die
 * uebrigen entfallen (jede offene Zahlung braucht mindestens einen Cent). Sonst anteilig
 * (`verteileNeu`, Verfahren des groessten Restes).
 */
function verteileGruppe(zeilen: readonly OffeneZeile[], ziel: number): NachtragZahlungsAktion[] {
  if (zeilen.length === 0) return [];
  const aus = (z: OffeneZeile, neuCents: number): NachtragZahlungsAktion => ({
    zahlungId: z.id,
    typ: z.typ,
    altCents: z.altCents,
    neuCents,
    aktion: neuCents === 0 ? "entfallen" : neuCents === z.altCents ? "behalten" : "ersetzen",
  });
  if (ziel <= 0) return zeilen.map((z) => aus(z, 0));
  if (ziel < zeilen.length) return zeilen.map((z, i) => aus(z, i === 0 ? ziel : 0));
  const v = verteileNeu(
    ziel,
    zeilen.map((z) => ({ amountCents: z.altCents, status: "pending" })),
  );
  if (!v.ok) return zeilen.map((z, i) => aus(z, i === 0 ? ziel : 0));
  const proIndex = new Map(v.aenderungen.map((a) => [a.index, a.neuCents]));
  return zeilen.map((z, i) => aus(z, proIndex.get(i) ?? 0));
}

export function nachtragsAusgleich(eingabe: NachtragsAusgleichEingabe): NachtragsAusgleich {
  const { altGesamtCents, neuGesamtCents } = eingabe;
  if (!Number.isInteger(altGesamtCents) || !Number.isInteger(neuGesamtCents) || altGesamtCents < 0 || neuGesamtCents < 0) {
    return { ok: false, fehler: "ungueltige_summe", meldung: "Alter und neuer Gesamtbetrag muessen ganze Cent-Betraege sein (nicht negativ)." };
  }

  const sollAnzahlungCents = nachtragAnzahlungSollCents(eingabe.konditionen, neuGesamtCents) ?? 0;

  let netto = 0;
  // Keine neue Anzahlung, wenn die Anzahlungsseite schon "belegt" ist: eine Vollzahlung (full, auch bezahlt) deckt sie ab,
  // und eine fehlgeschlagene/erstattete Anzahlung legt der Ausgleich nicht auf Verdacht neu an (wie bisher). NUR eine
  // BEZAHLTE Anzahlung sperrt nicht: dann verlangt die neue Soll-Anzahlung die Differenz.
  let vollzahlungVorhanden = false;
  const anzahlungen: OffeneZeile[] = [];
  const restzahlungen: OffeneZeile[] = [];
  for (const z of eingabe.zahlungen) {
    netto += nettoBezahlt(z);
    const art = zahlungsArt(z.typ);
    if (z.status !== "canceled" && (z.typ === "full" || (art === "deposit" && z.status !== "succeeded"))) vollzahlungVorhanden = true;
    if (!istOffenerZahlungsstatus(z.status)) continue;
    if (art === null) {
      return { ok: false, fehler: "unbekannte_zahlungsart", meldung: "Eine offene Zahlung hat keine bekannte Zahlungsart (deposit/prepayment/final/full) - der Ausgleich verteilt nicht auf Verdacht." };
    }
    const zeile = { id: z.id, typ: z.typ ?? null, altCents: Math.max(ganz(z.amountCents), 0) };
    (art === "deposit" ? anzahlungen : restzahlungen).push(zeile);
  }

  const rest = Math.max(neuGesamtCents - netto, 0);
  const ueberschuss = Math.max(netto - neuGesamtCents, 0);

  const aktionen: NachtragZahlungsAktion[] = [];
  if (rest === 0) {
    aktionen.push(...verteileGruppe(anzahlungen, 0), ...verteileGruppe(restzahlungen, 0));
  } else {
    // Anzahlung: nach den neuen Konditionen; ohne Konditionen bleibt eine von Hand angelegte
    // Anzahlung, hoechstens auf den neuen Rest gedeckelt.
    const anzahlungOffenAlt = anzahlungen.reduce((s, z) => s + z.altCents, 0);
    const wunsch = sollAnzahlungCents > 0 ? Math.max(sollAnzahlungCents - netto, 0) : anzahlungOffenAlt;
    const anzahlungZiel = Math.min(wunsch, rest);
    let neueAnzahlung = 0;
    if (anzahlungen.length > 0) {
      aktionen.push(...verteileGruppe(anzahlungen, anzahlungZiel));
    } else if (!vollzahlungVorhanden && sollAnzahlungCents > 0 && anzahlungZiel > 0) {
      // Auch bei BEREITS BEZAHLTER Anzahlung: die neue Soll-Anzahlung verlangt die Differenz (Mehrbetrag-Nachtrag).
      neueAnzahlung = anzahlungZiel;
      aktionen.push({ zahlungId: null, aktion: "neu_anlegen", typ: "deposit", altCents: 0, neuCents: anzahlungZiel });
    }
    // Restzahlung: nur bestehende offene Zeilen anpassen. Fehlt sie, entsteht sie spaeter aus dem
    // Gesamtbetrag (auto-invoice / Restzahlungs-Nachlauf); der Rest steht in nochZuZahlenCents.
    if (restzahlungen.length > 0) {
      const belegt = anzahlungen.length > 0 ? anzahlungZiel : neueAnzahlung;
      aktionen.push(...verteileGruppe(restzahlungen, rest - belegt));
    }
  }

  // Mehrbetrag bei bisher VOLLSTAENDIG bezahltem Vorgang (z. B. 1.000 EUR bezahlt, neu 1.200 EUR): wenn danach KEINE offene Zeile den
  // Rest traegt (keine Anzahlung, keine Restzahlung, keine neue Anzahlung), entsteht eine Restzahlung ueber den Rest - sonst
  // bliebe der Mehrbetrag ohne Zahlung und der Vorgang laege dauerhaft zwischen bezahlt und offen. NUR in genau diesem Fall.
  const traegtRest = aktionen.some((a) => a.aktion !== "entfallen" && a.neuCents > 0);
  if (rest > 0 && !traegtRest && netto >= altGesamtCents && netto > 0 && neuGesamtCents > altGesamtCents) {
    aktionen.push({ zahlungId: null, aktion: "neu_anlegen", typ: "final", altCents: 0, neuCents: rest });
  }

  // Sofort faellig: Anzahlungen nach dem Ausgleich plus eine neu angelegte Restzahlung (Mehrbetrag-Fall oben).
  const faellig = aktionen.filter((a) => zahlungsArt(a.typ) === "deposit" || a.aktion === "neu_anlegen").reduce((sum, a) => sum + a.neuCents, 0);
  return {
    ok: true,
    altGesamtCents,
    neuGesamtCents,
    differenzCents: neuGesamtCents - altGesamtCents,
    sollAnzahlungCents,
    bereitsBezahltNettoCents: netto,
    nochZuZahlenCents: rest,
    mehrbetragCents: Math.max(neuGesamtCents - altGesamtCents, 0),
    ueberschussCents: ueberschuss,
    faelligNachZustimmungCents: faellig,
    restCents: Math.max(rest - faellig, 0),
    aktionen,
  };
}

// ---------------------------------------------------------------------------------------------
// Vergleich
// ---------------------------------------------------------------------------------------------

/** Eine Position aus `offer_history.options_snapshot[].items` (nur die gelesenen Felder). */
export interface NachtragPositionSnapshot {
  id?: string | null;
  name: string;
  section?: string | null;
  dayNo?: number | null;
  catalogItemId?: string | null;
  packageId?: string | null;
  qtyMilli?: number | null;
  unitPriceCents?: number | null;
  unit?: string | null;
  isOptional?: boolean | null;
}

/** Eine Variante aus `offer_history.options_snapshot[]`. */
export interface NachtragOptionSnapshot {
  id?: string | null;
  amountTotalCents?: number | null;
  guestCount?: number | null;
  items?: readonly NachtragPositionSnapshot[] | null;
}

/** Die Felder aus `offer_history.event_snapshot`, die der Vergleich liest. */
export interface NachtragEventSnapshot {
  guestCount?: number | null;
  eventDate?: string | Date | null;
}

/** Ein Stand: die GEWAEHLTE Variante der Fassung samt Event-Snapshot. */
export interface NachtragStand {
  option: NachtragOptionSnapshot;
  event?: NachtragEventSnapshot | null;
}

export interface PositionsStand {
  /** Kundensichtbare Bezeichnung der Position (eine Umbenennung ist eine Aenderung). */
  bezeichnung: string;
  mengeMilli: number;
  preisCents: number;
  einheit: string | null;
  optional: boolean;
}

export interface PositionsAenderung {
  art: "neu" | "entfallen" | "geaendert";
  name: string;
  section: string | null;
  dayNo: number | null;
  alt: PositionsStand | null;
  neu: PositionsStand | null;
}

export interface NachtragsVergleich {
  positionen: PositionsAenderung[];
  gaeste: { alt: number | null; neu: number | null; geaendert: boolean };
  /** ISO-Datum (JJJJ-MM-TT) oder null. */
  datum: { alt: string | null; neu: string | null; geaendert: boolean };
  summe: { altCents: number; neuCents: number; differenzCents: number };
  hatAenderungen: boolean;
}

const BERLIN_TAG = new Intl.DateTimeFormat("sv-SE", { timeZone: "Europe/Berlin", year: "numeric", month: "2-digit", day: "2-digit" });

/** Kalendertag (JJJJ-MM-TT). Zeitpunkte (Date oder ISO mit Uhrzeit) zaehlen nach Europe/Berlin, nicht nach UTC. */
function datumIso(v: string | Date | null | undefined): string | null {
  if (v == null) return null;
  const alsDatum = v instanceof Date ? v : /^\d{4}-\d{2}-\d{2}$/.test(String(v).trim()) ? null : new Date(String(v));
  if (alsDatum) return Number.isNaN(alsDatum.getTime()) ? null : BERLIN_TAG.format(alsDatum);
  return String(v).trim();
}

function zahlOderNull(v: number | null | undefined): number | null {
  return typeof v === "number" && Number.isFinite(v) ? v : null;
}

const stand = (it: NachtragPositionSnapshot): PositionsStand => ({
  bezeichnung: it.name.trim(),
  mengeMilli: ganz(it.qtyMilli ?? 1000),
  preisCents: ganz(it.unitPriceCents),
  einheit: it.unit ?? null,
  optional: it.isOptional === true,
});

const gleich = (a: PositionsStand, b: PositionsStand) =>
  a.bezeichnung === b.bezeichnung && a.mengeMilli === b.mengeMilli && a.preisCents === b.preisCents && a.einheit === b.einheit && a.optional === b.optional;

/** Gruppenschluessel einer Position: Tag, Bereich, Katalog-/Paket-ID, sonst Name. */
function gruppenSchluessel(it: NachtragPositionSnapshot): string {
  const wer = it.catalogItemId ?? it.packageId ?? it.name.trim().toLowerCase();
  return `${it.dayNo ?? ""}|${it.section ?? ""}|${wer}`;
}

function gruppiere(items: readonly NachtragPositionSnapshot[] | null | undefined): Map<string, NachtragPositionSnapshot[]> {
  const g = new Map<string, NachtragPositionSnapshot[]>();
  for (const it of items ?? []) {
    const k = gruppenSchluessel(it);
    g.set(k, [...(g.get(k) ?? []), it]);
  }
  return g;
}

export function nachtragsVergleich(alt: NachtragStand, neu: NachtragStand): NachtragsVergleich {
  const a = gruppiere(alt.option.items);
  const n = gruppiere(neu.option.items);
  const positionen: PositionsAenderung[] = [];
  const meta = (it: NachtragPositionSnapshot) => ({ name: it.name, section: it.section ?? null, dayNo: it.dayNo ?? null });

  for (const k of new Set([...n.keys(), ...a.keys()])) {
    const rechts = [...(n.get(k) ?? [])];
    const links = [...(a.get(k) ?? [])];
    // Dubletten bestmoeglich zuordnen: erst exakte Treffer (Menge, Preis, Einheit, optional), dann der Rest der Reihe nach.
    for (let i = rechts.length - 1; i >= 0; i -= 1) {
      const j = links.findIndex((l) => gleich(stand(l), stand(rechts[i] as NachtragPositionSnapshot)));
      if (j >= 0) {
        links.splice(j, 1);
        rechts.splice(i, 1);
      }
    }
    const paare = Math.min(links.length, rechts.length);
    for (let i = 0; i < paare; i += 1) {
      const l = links[i] as NachtragPositionSnapshot;
      const r = rechts[i] as NachtragPositionSnapshot;
      positionen.push({ art: "geaendert", ...meta(r), alt: stand(l), neu: stand(r) });
    }
    for (const r of rechts.slice(paare)) positionen.push({ art: "neu", ...meta(r), alt: null, neu: stand(r) });
    for (const l of links.slice(paare)) positionen.push({ art: "entfallen", ...meta(l), alt: stand(l), neu: null });
  }

  // Die Gaestezahl der GEWAEHLTEN Variante zaehlt (sie traegt Preis je Person und Teilgruppen),
  // die des Vorgangs nur als Rueckfall.
  const gAlt = zahlOderNull(alt.option.guestCount) ?? zahlOderNull(alt.event?.guestCount);
  const gNeu = zahlOderNull(neu.option.guestCount) ?? zahlOderNull(neu.event?.guestCount);
  const dAlt = datumIso(alt.event?.eventDate);
  const dNeu = datumIso(neu.event?.eventDate);
  const sAlt = ganz(alt.option.amountTotalCents);
  const sNeu = ganz(neu.option.amountTotalCents);

  const gaeste = { alt: gAlt, neu: gNeu, geaendert: gAlt !== gNeu };
  const datum = { alt: dAlt, neu: dNeu, geaendert: dAlt !== dNeu };
  return {
    positionen,
    gaeste,
    datum,
    summe: { altCents: sAlt, neuCents: sNeu, differenzCents: sNeu - sAlt },
    hatAenderungen: positionen.length > 0 || gaeste.geaendert || datum.geaendert || sAlt !== sNeu,
  };
}
